/**
 *  Games launcher
 */

define(function(require, exports, module) {
    'use strict';
    const core = require('core');
    const i18n = require('i18n');
    const user = require('user');
    const config = require('config');
    const server = require('server');
    const url = require('util/url.util');
    const device = require('util/device.util');
    const storage = require('util/storage.util');
    const serverUtil = require('util/server.util');
    var BaseModel = require('common/base.model');
    const Base3rdPartyPlatform = require('modules/launcher/platforms/base.3rdParty.platform');

    module.exports = {

        /**
         * Game that launched right now
         * @type {Object}
         */
        launchedGame: null,

        originalGameMode: null,

        /**
         * Launch game
         * @param  {string} gameCode Model of Game
         * @param  {number} mode "Real" or "Demo" mode for game
         * @param  {string=} querystring - additional URL params
         */
        launchGame: function(gameCode, mode, queryString, options) {
            const { ageVerificationStatus } = user.get('userDetails');
            const isDataVerificationStatusNeeded = config.get('enableDataVerificationStatusCheck')
                && ['failed', 'review', 'unknown'].includes(ageVerificationStatus);

            // disable Game launch for Admin
            if (user.isAdmin()) {
                window.Playtech.API.popup.showPopup({
                    title: i18n.t('playtech.mobile.system.launcher.gameLaunchPopup.title'),
                    content: i18n.t('playtech.mobile.system.launcher.gameLaunchPopup.content')
                });

                return;
            }

            if (Playtech.API.htcmd.isEnabled) {
                Playtech.API.htcmd.launchgame({ code: gameCode, mode });
                return;
            }

            if (isDataVerificationStatusNeeded) {
                console.warn('Game playing is disabled because of "DataVerificationStatus" verification is needed');

                // in case loginPage is configured
                Base3rdPartyPlatform.navigateFromLaunchGamePath();

                return;
            }

            // continue register for fast registered players
            if (user.isFastRegistered()) {
                window.Playtech.API.popup.closePopups();

                return user.continueRegistration(true,
                    {
                        code: gameCode,
                        mode: mode,
                        queryString: queryString,
                        options: options
                    });
            }

            const game = this._getGame(gameCode);

            if (!game) {
                Base3rdPartyPlatform.navigateFromLaunchGamePath();

                return;
            }

            // Real mode by default
            mode = mode !== undefined ? mode : 1;

            // If we try to open demo mode need to check if it's available
            if (!mode || this.originalGameMode === 0) {
                this.originalGameMode = mode;

                var gameModesMap = {
                    guest: Number(user.isLoggedIn()),
                    user: Number(!user.isLoggedIn()),
                    both: 0,
                    unavailable: 1
                };

                mode = gameModesMap[game.demoModeSupport];
            }

            if (mode && !user.isLoggedIn()) {
                if (config.get('loginPage')) {
                    storage.session.setItem('gamesPageUrl', url.getCleanUrl(window.location.pathname));
                }

                // Open login popup for guest
                Playtech.API.auth.showLogin({
                    state: { gameLaunchData: options },
                    afterLoginUrl: '/igaming/' + gameCode,
                    onLogin: () => this._handleSuccessLogin(gameCode, mode, queryString, options)
                });

                return;
            }

            window.Playtech.API.popup.closePopups('gameLaunch');

            this.originalGameMode = null;

            return this.launchWithLimitsVerification(gameCode, mode, queryString, options);
        },

        launchWithLimitsVerification: async function(gameCode, mode, queryString, options) {
            const launchMethod = this._getLaucherMethod(gameCode);
            const isVerificationNeeded = mode === 1
                && config.get('enableGamblingSessionLimitPopup')
                && ['redirectInNewWindow', 'redirectInThisWindow'].includes(launchMethod);

            if (isVerificationNeeded) {
                const showGamblingSessionLimitsPopup = () => window.Playtech.API.popup.showPopup('gamblingSessionLimits', {
                    onSuccessLimitsSet: () => this._launch(gameCode, mode, queryString, options)
                });

                try {
                    const { data } = await server.send({
                        ID: 35899,
                        responses: [35900, 35901],
                        errorLevel: 1,
                        description: 'Get gambling session limits'
                    });

                    const action = data?.actions?.find(item => item.actionShowSimpleDialogInfo)?.actionShowSimpleDialogInfo;

                    if (action) {
                        window.Playtech.API.popup.showPopup('simpleDialogInfo', {
                            message: action.message,
                            actionId: action.actionId,
                            layer: 'GAMBLING_SESSION',
                            onAccept: showGamblingSessionLimitsPopup,
                            acceptBtnText: i18n.t('playtech.mobile.system.gamblingSessionLimits.intervalPopup.submit'),
                            declineBtnText: i18n.t('playtech.mobile.system.gamblingSessionLimits.intervalPopup.cancel')
                        });

                        return;
                    }

                    if (!data?.gamblingSessionId) {
                        showGamblingSessionLimitsPopup();

                        return;
                    }
                } catch (error) {
                    window.Playtech.API.popup.showPopup({
                        title: 'playtech.mobile.system.gamblingSessionLimits.errorPopup.title',
                        content: serverUtil.getErrorMessage(error)
                    });

                    return;
                }
            }

            return this._launch(gameCode, mode, queryString, options);
        },

        /**
         * Get launched method of game
         * @param  {string}  gameCode
         * @return {string}
         */
        _getLaucherMethod: function(gameCode) {
            var game = config.get('games')[gameCode];
            var gameType = !device.isDesktop ? game.mobileGameType : game.desktopGameType;
            var launcherOptions = Base3rdPartyPlatform.getLaunchOptions(gameType);

            if (!launcherOptions) {
                return core.error(`Launcher options for: ${gameType} game type are not configured in admin area`);
            }

            return !device.isDesktop ? launcherOptions.launchMethodMobile : launcherOptions.launchMethodDesktop;
        },

        _isLaunchInNewWindow: function(gameCode) {
            var launchedMethod = this._getLaucherMethod(gameCode);

            return launchedMethod === 'redirectInNewWindow';

        },

        /**
         * Get game from global configuration by gameCode
         * @param  {string} gameCode
         * @return {Object} object with game info
         */
        _getGame: function(gameCode) {
            const game = config.get('games')[gameCode];

            if (!game) {
                core.error(`Game with game code: ${gameCode} is not configured`);

                return;
            }

            if (!device.isDesktop) {
                game.mobileGameCode = game.mobileGameCode || game.gameCode;
            }

            return game;
        },

        /**
         * Get url with params for game launch
         * @private
         * @param  {string} gameCode -- game code in gamesConfiguration
         * @param  {number} mode     -- real or demo mode
         * @param  {string=} querystring -- additional URL params
         */
        _launch: function(gameCode, mode, queryString, options) {
            var game = this._getGame(gameCode);

            if (!game) {
                return;
            }

            user.set({
                launchedGame: {
                    queryString: queryString,
                    code: gameCode,
                    mode: mode,
                    options: options
                }
            });

            Base3rdPartyPlatform.setLauncher(this);
            Base3rdPartyPlatform.setAdditionalQueryString(queryString);

            setTimeout(() => {
                core.trigger('game-launch-event', { game, mode, options });
            }, 50);

            Base3rdPartyPlatform.open(new BaseModel({ isInFavorites: false, ...game, idAttribute: 'gameCode' }), mode);

            if (options?.onGameLaunched) {
                options.onGameLaunched();
            }
        },

        _handleSuccessLogin: function(gameCode, mode, queryString, options) {

            if (!device.isDesktop) {
                return this.launchGame(gameCode, mode, queryString, options);
            }

            if (this._isLaunchInNewWindow(gameCode)) {
                var game = this._getGame(gameCode);
                var buttonTitle = i18n.t('playtech.mobile.system.button.realPlay') + ' ' + game.name;
                var popupContent = i18n.t('playtech.mobile.system.games.popup-blocker-with-confirm');

                window.Playtech.API.popup.showPopup({
                    content: popupContent,
                    buttons: [
                        {
                            text: buttonTitle,
                            onClick: () => this.launchGame(gameCode, mode, queryString, options)
                        }
                    ],
                    onClose: function() {
                        core.trigger('page:refresh');
                    }
                });
            } else {
                this.launchGame(gameCode, mode, queryString, options);
            }
        }
    };
});
